import { useMoneyFormatter } from "@/lib/money-format";
import { useEffect, useMemo, useState } from "react";
import { Link, createFileRoute } from "@tanstack/react-router";
import { Loader2, Plus, ShoppingCart } from "lucide-react";
import { useTranslation } from "react-i18next";
import { RequireAuth } from "@/components/RequireAuth";
import { ErrorState } from "@/components/ErrorState";
import { AppShell } from "@/components/layout/AppShell";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { TerceroLookup } from "@/components/shared";

import { PURCHASE_STATUS_BADGE_VARIANT } from "@/components/purchases/format";
import {
  usePurchaseInvoice,
  usePurchaseInvoices,
} from "@/lib/api/purchase-invoices.queries";
import { useActiveCompany } from "@/lib/company-context";
import type { PurchaseInvoiceStatusFilter } from "@/lib/api/purchase-invoices";
import type { TerceroSummary } from "@/lib/api/terceros";

export const Route = createFileRoute("/companies/$companyId/compras/")({
  component: PurchasesIndexPage,
});

const STATUS_FILTERS: PurchaseInvoiceStatusFilter[] = [
  "all",
  "draft",
  "posted",
  "void",
];

function PurchasesIndexPage() {
  return (
    <RequireAuth>
      <AppShell>
        <PurchasesIndexContent />
      </AppShell>
    </RequireAuth>
  );
}

function PurchasesIndexContent() {
  const formatCop = useMoneyFormatter();
  const { t } = useTranslation();
  const { companyId } = Route.useParams();
  const { setActiveCompanyId } = useActiveCompany();

  useEffect(() => {
    if (companyId) setActiveCompanyId(companyId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId]);

  const [q, setQ] = useState("");
  const [status, setStatus] = useState<PurchaseInvoiceStatusFilter>("all");
  const [supplier, setSupplier] = useState<TerceroSummary | null>(null);
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [page, setPage] = useState(1);

  const filters = useMemo(
    () => ({
      ...(q.trim() ? { q: q.trim() } : {}),
      status,
      ...(supplier ? { supplierTerceroId: supplier.id } : {}),
      ...(dateFrom ? { dateFrom } : {}),
      ...(dateTo ? { dateTo } : {}),
      page,
      pageSize: 50,
    }),
    [q, status, supplier, dateFrom, dateTo, page],
  );

  const listQuery = usePurchaseInvoices(companyId, filters);
  const invoices = listQuery.data?.invoices ?? [];
  const total = listQuery.data?.pagination.total ?? 0;
  const pageSize = listQuery.data?.pagination.pageSize ?? 50;
  const pageCount = Math.max(1, Math.ceil(total / pageSize));
  const hasFilters =
    q.trim().length > 0 || status !== "all" || !!supplier || !!dateFrom || !!dateTo;

  return (
    <div className="mx-auto flex max-w-6xl flex-col gap-6">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">
            {t("purchases.list.title")}
          </h1>
          <p className="mt-1 text-sm text-muted-foreground">
            {t("purchases.list.subtitle")}
          </p>
        </div>
        <Button asChild>
          <Link to="/companies/$companyId/compras/new" params={{ companyId }}>
            <Plus className="h-4 w-4" />
            {t("purchases.list.new")}
          </Link>
        </Button>
      </div>

      <Card>
        <CardContent className="grid gap-3 pt-6 md:grid-cols-5">
          <div>
            <Label htmlFor="purchases-q">{t("purchases.list.filter_q")}</Label>
            <Input
              id="purchases-q"
              value={q}
              onChange={(e) => {
                setQ(e.target.value);
                setPage(1);
              }}
              placeholder={t("purchases.list.filter_q_placeholder")}
            />
          </div>
          <div>
            <Label htmlFor="purchases-supplier">
              {t("purchases.list.filter_supplier")}
            </Label>
            <TerceroLookup
              id="purchases-supplier"
              companyId={companyId}
              kind="supplier"
              value={supplier}
              onChange={(next) => {
                setSupplier(next);
                setPage(1);
              }}
              allowCreate={false}
            />
          </div>
          <div>
            <Label htmlFor="purchases-status">
              {t("purchases.list.filter_status")}
            </Label>
            <Select
              value={status}
              onValueChange={(v) => {
                setStatus(v as PurchaseInvoiceStatusFilter);
                setPage(1);
              }}
            >
              <SelectTrigger id="purchases-status">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {STATUS_FILTERS.map((s) => (
                  <SelectItem key={s} value={s}>
                    {t(`purchases.statusFilter.${s}`)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label htmlFor="purchases-from">
              {t("purchases.list.filter_from")}
            </Label>
            <Input
              id="purchases-from"
              type="date"
              value={dateFrom}
              onChange={(e) => {
                setDateFrom(e.target.value);
                setPage(1);
              }}
            />
          </div>
          <div>
            <Label htmlFor="purchases-to">{t("purchases.list.filter_to")}</Label>
            <Input
              id="purchases-to"
              type="date"
              value={dateTo}
              onChange={(e) => {
                setDateTo(e.target.value);
                setPage(1);
              }}
            />
          </div>
        </CardContent>
      </Card>

      {listQuery.isPending ? (
        <div className="flex items-center justify-center rounded-lg border bg-card py-16">
          <Loader2 className="h-6 w-6 animate-spin text-primary" />
        </div>
      ) : listQuery.isError ? (
        <ErrorState
          title={t("purchases.list.error_title")}
          description={t("purchases.list.error_body")}
          error={listQuery.error}
          onRetry={() => void listQuery.refetch()}
          retryLabel={t("common.retry")}
        />
      ) : invoices.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center gap-4 py-16 text-center">
            <div className="flex h-16 w-16 items-center justify-center rounded-full bg-primary/10">
              <ShoppingCart className="h-8 w-8 text-primary" />
            </div>
            <div className="max-w-sm space-y-1">
              <h2 className="text-lg font-semibold">
                {hasFilters
                  ? t("purchases.list.empty_search_title")
                  : t("purchases.list.empty_title")}
              </h2>
              <p className="text-sm text-muted-foreground">
                {hasFilters
                  ? t("purchases.list.empty_search_body")
                  : t("purchases.list.empty_body")}
              </p>
            </div>
            {!hasFilters ? (
              <Button asChild>
                <Link
                  to="/companies/$companyId/compras/new"
                  params={{ companyId }}
                >
                  <Plus className="h-4 w-4" />
                  {t("purchases.list.new")}
                </Link>
              </Button>
            ) : null}
          </CardContent>
        </Card>
      ) : (
        <Card>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t("purchases.table.number")}</TableHead>
                <TableHead>{t("purchases.table.supplier")}</TableHead>
                <TableHead>{t("purchases.table.issue_date")}</TableHead>
                <TableHead className="text-right">
                  {t("purchases.table.payable")}
                </TableHead>
                <TableHead>{t("purchases.table.status")}</TableHead>
                <TableHead>{t("purchases.table.payment")}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {invoices.map((invoice) => (
                <TableRow key={invoice.id}>
                  <TableCell>
                    <Link
                      to="/companies/$companyId/compras/$invoiceId"
                      params={{ companyId, invoiceId: invoice.id }}
                      className="font-mono text-sm font-medium text-primary hover:underline"
                    >
                      {invoice.invoiceNumber}
                    </Link>
                    {invoice.documentTypeCode && invoice.documentTypeCode !== "01" ? (
                      <Badge variant="outline" className="ml-2 text-[10px]">
                        {t("purchases.equivalente_badge")}
                      </Badge>
                    ) : null}
                    {invoice.source === "import" ? (
                      <p className="text-[10px] text-muted-foreground">
                        {t("purchases.imported_tag")}
                      </p>
                    ) : null}
                  </TableCell>
                  <TableCell>
                    <div className="max-w-[220px]">
                      <p className="truncate text-sm">
                        {invoice.supplierLegalName ?? "—"}
                      </p>
                      <p className="font-mono text-xs text-muted-foreground">
                        {invoice.supplierIdentificationNumber ?? ""}
                      </p>
                    </div>
                  </TableCell>
                  <TableCell className="text-sm">{invoice.issueDate}</TableCell>
                  <TableCell className="text-right font-mono text-sm">
                    {formatCop(invoice.payableAmount)}
                  </TableCell>
                  <TableCell>
                    <Badge variant={PURCHASE_STATUS_BADGE_VARIANT[invoice.status]}>
                      {invoice.source === "import" && invoice.status === "draft"
                        ? t("purchases.status.imported_draft")
                        : t(`purchases.status.${invoice.status}`)}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    {invoice.status === "posted" ? (
                      <PaymentStatusBadge
                        companyId={companyId}
                        invoiceId={invoice.id}
                      />
                    ) : (
                      <span className="text-xs text-muted-foreground">—</span>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          {pageCount > 1 ? (
            <div className="flex items-center justify-between border-t px-4 py-3 text-sm">
              <span className="text-muted-foreground">
                {t("purchases.list.page_of", { page, pageCount, total })}
              </span>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={page <= 1}
                  onClick={() => setPage((p) => p - 1)}
                >
                  {t("common.back")}
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={page >= pageCount}
                  onClick={() => setPage((p) => p + 1)}
                >
                  {t("common.next")}
                </Button>
              </div>
            </div>
          ) : null}
        </Card>
      )}
    </div>
  );
}

/**
 * Derived payment badge for POSTED rows. The list endpoint doesn't carry
 * paidAmount (it is derived per invoice from voucher applications), so the
 * badge reads the cached detail query per row — react-query dedupes and the
 * per-company DO answers these in single-digit ms. Revisit if lists grow
 * beyond pre-stable volumes.
 */
function PaymentStatusBadge({
  companyId,
  invoiceId,
}: {
  companyId: string;
  invoiceId: string;
}) {
  const { t } = useTranslation();
  const detailQuery = usePurchaseInvoice(companyId, invoiceId);
  if (!detailQuery.data) {
    return <span className="text-xs text-muted-foreground">…</span>;
  }
  const status = detailQuery.data.paymentStatus;
  const variant =
    status === "paid" ? "success" : status === "partial" ? "warning" : "outline";
  return (
    <Badge variant={variant}>{t(`purchases.paymentStatus.${status}`)}</Badge>
  );
}
